'use client';

import Link from 'next/link';
import { useState } from 'react';
import { MenuIcon } from '@/components/icons';
import type { Auth0User } from '@/hooks/useAuth';
import { useAuth } from '@/hooks/useAuth';
import { useHasRole } from '@/hooks/useCurrentUserRoles';
import UserDropdown from './UserDropdown';

export default function MobileMenu({
  user,
  profileImageUrl,
}: {
  // Accept null from upstream (useUser can yield null) to satisfy ClientHeader usage
  user?: Auth0User | null;
  profileImageUrl?: string | null;
}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { user: authUser } = useAuth();

  // Use Auth0 user if available, otherwise use passed user prop
  const currentUser = authUser || user;

  // Navigation role checks - destructure hasRole and isLoading from each hook call
  // We track loading state to prevent menu items from flashing during initial role load
  const { hasRole: hasPatients, isLoading: patientsLoading } = useHasRole([
    'Patients',
    'iSleep',
  ]); // iSleep grants patient chart access
  const { hasRole: hasTraining, isLoading: trainingLoading } =
    useHasRole('Trianing'); // Note: typo in database
  const { hasRole: hasAlora, isLoading: aloraLoading } = useHasRole('Alora');

  // Sax role — gates Manage, Admin, Operations sections and Outreach link
  const { hasRole: isSaxUser, isLoading: saxUserLoading } = useHasRole('Sax');

  // Combine loading states for roles whose values are actually used
  const isRolesLoading =
    patientsLoading ||
    trainingLoading ||
    aloraLoading ||
    saxUserLoading;

  // Manage section is gated on Sax role only
  const hasAnyManageAccess = !isRolesLoading && isSaxUser;

  // Admin hub is gated on Sax role only
  const hasAnyAdminAccess = !isRolesLoading && isSaxUser;

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Mobile menu button */}
      <button
        aria-controls="navbar-hamburger"
        aria-expanded={isMenuOpen}
        className="inline-flex items-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 lg:hidden"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        type="button"
      >
        <span className="sr-only">Open main menu</span>
        <MenuIcon className="h-6 w-6" />
      </button>

      {/* Mobile menu */}
      <div
        className={`${isMenuOpen ? 'block' : 'hidden'} absolute left-0 top-[72px] z-10 w-full bg-white dark:bg-gray-900 lg:hidden max-h-[calc(100vh-72px)] overflow-y-auto`}
        id="navbar-hamburger"
      >
        {/* Mobile actions */}
        <div className="flex items-center justify-end border-b border-gray-200 p-4 dark:border-gray-700">
          <UserDropdown
            isMobile={true}
            profileImageUrl={profileImageUrl}
            user={currentUser}
          />
        </div>

        {/* Mobile navigation links */}
        <ul className="flex flex-col font-medium">
          {/* Always visible */}
          <li>
            <Link
              className="block border-b border-gray-100 py-2 pl-3 pr-4 text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
              href="/dashboard"
              onClick={closeMenu}
            >
              Dashboard
            </Link>
          </li>

          {/* Always visible */}
          <li>
            <Link
              className="block border-b border-gray-100 py-2 pl-3 pr-4 text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
              href="/history"
              onClick={closeMenu}
            >
              History
            </Link>
          </li>

          {/* Role-based: Patients - Only show when roles are loaded to prevent flash */}
          {!isRolesLoading && hasPatients && (
            <li>
              <Link
                className="block border-b border-gray-100 py-2 pl-3 pr-4 text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
                href="/patients"
                onClick={closeMenu}
              >
                Patients
              </Link>
            </li>
          )}

          {/* Role-based: Outreach (Sax only) */}
          {!isRolesLoading && isSaxUser && (
            <li>
              <Link
                className="block border-b border-gray-100 py-2 pl-3 pr-4 text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
                href="/outreach/conversations"
                onClick={closeMenu}
                rel="noopener noreferrer"
                target="_blank"
              >
                Outreach
              </Link>
            </li>
          )}

          {/* Always visible */}
          <li>
            <Link
              className="block border-b border-gray-100 py-2 pl-3 pr-4 text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
              href="/support"
              onClick={closeMenu}
            >
              Support
            </Link>
          </li>

          {/* Role-based: Training - Only show when roles are loaded */}
          {!isRolesLoading && hasTraining && (
            <li>
              <Link
                className="block border-b border-gray-100 py-2 pl-3 pr-4 text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
                href="/training"
                onClick={closeMenu}
              >
                Training
              </Link>
            </li>
          )}

          {/* Role-based: Alora - Only show when roles are loaded */}
          {!isRolesLoading && hasAlora && (
            <li>
              <Link
                className="block border-b border-gray-100 py-2 pl-3 pr-4 text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
                href="/bot"
                onClick={closeMenu}
              >
                Alora
              </Link>
            </li>
          )}

          {/* Manage Section - Link to manage hub page */}
          {hasAnyManageAccess && (
            <li>
              <Link
                className="block border-b border-gray-100 py-2 pl-3 pr-4 font-semibold text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
                href="/admin/manage"
                onClick={closeMenu}
              >
                Manage
              </Link>
            </li>
          )}

          {/* Admin Section - Link to admin hub page */}
          {hasAnyAdminAccess && (
            <li>
              <Link
                className="block border-b border-gray-100 py-2 pl-3 pr-4 font-semibold text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
                href="/admin"
                onClick={closeMenu}
              >
                Admin
              </Link>
            </li>
          )}

          {/* Operations Section - Link to operations hub page (Sax users only) */}
          {!isRolesLoading && isSaxUser && (
            <li>
              <Link
                className="block border-b border-gray-100 py-2 pl-3 pr-4 font-semibold text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:text-white dark:hover:bg-gray-700"
                href="/operations"
                onClick={closeMenu}
              >
                Operations
              </Link>
            </li>
          )}

        </ul>
      </div>
    </>
  );
}
